import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import {
  FaClock, FaClipboardCheck, FaMapMarkedAlt, FaSignInAlt,
  FaSignOutAlt, FaArrowUp, FaFire, FaRoute, FaMapPin, FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { sitesAPI, checkinsAPI, statisticsAPI, reportsAPI } from '../../utils/api';
import '../../styles/modern-design-system.css';
import './PersonnelDashboard.css';

// Mobile Site Card Component
const SiteCard = ({ site, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className="site-card-mobile"
      style={{
        animation: `fade-in 0.3s ease-out ${index * 0.05}s forwards`,
        opacity: 0
      }}
    >
      <div
        className="site-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className="site-card-main">
          <div className="site-card-title">
            <FaMapPin style={{ color: 'var(--accent-blue)', marginRight: '0.5rem', flexShrink: 0 }} />
            <strong>{site.name}</strong>
          </div>
          <div className="site-card-meta">
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Last visited: {site.lastVisited}
            </span>
          </div>
        </div>
        <div className="site-card-actions">
          <span className={`status-badge ${site.status === 'visited' ? 'status-badge-success' : 'status-badge-error status-badge-pulse'
            }`} style={{ fontSize: 'var(--font-size-xs)', padding: '0.25rem 0.5rem' }}>
            {site.status === 'visited' ? '✓' : '!'}
          </span>
          {isExpanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
        </div>
      </div>

      {isExpanded && (
        <div className="site-card-details">
          <div className="site-card-detail-row">
            <span className="detail-label">Address:</span>
            <span className="detail-value">{site.address}</span>
          </div>
          <div className="site-card-detail-row">
            <span className="detail-label">Status:</span>
            <span className={`status-badge ${site.status === 'visited' ? 'status-badge-success' : 'status-badge-error'
              }`}>
              {site.status === 'visited' ? '✓ Visited' : '! Not Visited'}
            </span>
          </div>
          <Link
            to="/personnel/check"
            className="site-card-action-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: '0.75rem',
              padding: '0.75rem',
              background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))',
              color: 'white',
              borderRadius: 'var(--radius-md)',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 'var(--font-size-sm)',
              transition: 'all var(--transition-fast)'
            }}
          >
            <FaSignInAlt size={16} />
            <span>Quick Check-In</span>
          </Link>
        </div>
      )}
    </div>
  );
};


const PersonnelDashboardModern = () => {
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [recentSites, setRecentSites] = useState([]);
  const [stats, setStats] = useState({
    totalHours: 0,
    sitesVisited: 0,
    reportsSubmitted: 0,
    hoursChange: 0,
    sitesChange: 0,
    reportsChange: 0
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCheckInStatus = async () => {
      try {
        const response = await checkinsAPI.getActive();

        if (response.data?.active && response.data.checkIn?.id) {
          const checkInData = response.data.checkIn;
          setIsCheckedIn(true);
          setCurrentSession({
            checkInTime: checkInData.checkInTime,
            siteName: checkInData.site?.name || 'Unknown Site'
          });
        } else {
          setIsCheckedIn(false);
          setCurrentSession(null);
        }
      } catch (error) {
        console.error("Failed to fetch check-in status:", error);
        setIsCheckedIn(false);
        setCurrentSession(null);
      }
    };

    const fetchRecentSites = async () => {
      try {
        const sitesResponse = await sitesAPI.getAssigned();
        const checkInsResponse = await checkinsAPI.getHistory({ limit: 20 });

        const siteLastVisited = {};

        if (checkInsResponse.data && checkInsResponse.data.data && checkInsResponse.data.data.length > 0) {
          checkInsResponse.data.data.forEach(checkIn => {
            if (checkIn.siteId && !siteLastVisited[checkIn.siteId]) {
              siteLastVisited[checkIn.siteId] = checkIn.checkInTime;
            }
          });
        }

        const formattedSites = sitesResponse.data.map(site => ({
          id: site.id,
          name: site.name,
          address: site.address,
          lastVisited: siteLastVisited[site.id]
            ? new Date(siteLastVisited[site.id]).toLocaleDateString()
            : 'Not visited yet',
          status: siteLastVisited[site.id] ? 'visited' : 'pending'
        }));

        setRecentSites(formattedSites);
      } catch (error) {
        console.error("Failed to fetch assigned sites:", error);
      }
    };

    const fetchStats = async () => {
      try {
        const checkInsResponse = await checkinsAPI.getHistory({ timeRange: 'month' });
        const reportsResponse = await reportsAPI.getUserReports({ timeRange: 'month' });

        let totalHoursValue = 0;
        const visitedSiteIds = new Set();

        if (checkInsResponse && checkInsResponse.data && checkInsResponse.data.length > 0) {
          checkInsResponse.data.forEach(checkIn => {
            if (checkIn.siteId) {
              visitedSiteIds.add(checkIn.siteId);
            }

            if (checkIn.checkInTime && checkIn.checkOutTime) {
              const checkInTime = new Date(checkIn.checkInTime);
              const checkOutTime = new Date(checkIn.checkOutTime);
              const durationHours = (checkOutTime - checkInTime) / (1000 * 60 * 60);
              totalHoursValue += durationHours;
            }
          });
        }

        const reportsCount = reportsResponse?.data?.length || 0;

        const response = await statisticsAPI.getUserStats('month');

        if (response && response.data) {
          setStats({
            totalHours: Number(response.data.stats.totalHours ?? totalHoursValue).toFixed(1),
            sitesVisited: new Set(response.data.checkIns.map(ci => ci.site?.id)).size,
            reportsSubmitted: response.data.reports.length,
            hoursChange: 12,
            sitesChange: 8,
            reportsChange: 15
          });
        } else {
          setStats({
            totalHours: Number(totalHoursValue).toFixed(1),
            sitesVisited: visitedSiteIds.size,
            reportsSubmitted: reportsCount,
            hoursChange: 12,
            sitesChange: 8,
            reportsChange: 15
          });
        }
      } catch (error) {
        console.error("Failed to fetch stats:", error);
        setStats({
          totalHours: 0,
          sitesVisited: 0,
          reportsSubmitted: 0,
          hoursChange: 0,
          sitesChange: 0,
          reportsChange: 0
        });
      }
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchCheckInStatus(),
          fetchRecentSites(),
          fetchStats()
        ]);
        setError('');
      } catch (err) {
        setError('Failed to load dashboard data. Please try again later.');
        console.error('Dashboard data loading error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="personnel-dashboard-modern">
      <Container fluid className="px-3 px-md-4 px-lg-5" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        {/* Header */}
        <div className="animate-fade-in" style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 800,
            marginBottom: '0.5rem',
            background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-blue-light))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Personnel Dashboard
          </h1>
          <p style={{
            fontSize: 'var(--font-size-lg)',
            color: 'var(--text-muted)',
            fontWeight: 500
          }}>
            Welcome back! Here's your field activity overview.
          </p>
        </div>

        {error && (
          <Alert variant="danger" className="animate-slide-in" style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid var(--error-red)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: '2rem'
          }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" style={{
              width: '3rem',
              height: '3rem',
              color: 'var(--accent-blue)'
            }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p style={{ marginTop: '1rem', color: 'var(--text-muted)' }}>
              Loading your dashboard...
            </p>
          </div>
        ) : (
          <>
            {/* Hero Check-In/Out Card */}
            <div className="glass-card-hero animate-scale-in hero-card-mobile-optimized" style={{
              marginBottom: '2rem',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Row className="align-items-center g-3 g-md-4">
                <Col xs={12} lg={8}>
                  <div className="hero-status-section">
                    <div className="hero-status-header">
                      <div style={{
                        width: window.innerWidth < 768 ? '32px' : '20px',
                        height: window.innerWidth < 768 ? '32px' : '20px',
                        borderRadius: '50%',
                        background: isCheckedIn ? 'var(--success-emerald)' : 'var(--text-muted)',
                        boxShadow: isCheckedIn ? 'var(--glow-success)' : 'none',
                        animation: isCheckedIn ? 'pulse-badge 2s ease-in-out infinite' : 'none',
                        flexShrink: 0
                      }}></div>
                      <div className="hero-status-content">
                        <h2 style={{
                          fontSize: window.innerWidth < 768 ? 'clamp(1.25rem, 4vw, 1.5rem)' : 'clamp(1.5rem, 3vw, 2rem)',
                          fontWeight: 700,
                          marginBottom: isCheckedIn && currentSession ? '0.25rem' : 0,
                          color: 'var(--text-primary)',
                          lineHeight: 1.2
                        }}>
                          {isCheckedIn ? 'Currently Checked In' : 'Currently Checked Out'}
                        </h2>
                        {isCheckedIn && currentSession && (
                          <div className="hero-session-info">
                            <p style={{
                              fontSize: window.innerWidth < 768 ? 'var(--font-size-sm)' : 'var(--font-size-base)',
                              color: 'var(--text-secondary)',
                              marginBottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              flexWrap: 'wrap'
                            }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <FaMapPin style={{ color: 'var(--accent-blue)', flexShrink: 0 }} />
                                <strong>{currentSession.siteName}</strong>
                              </span>
                              <span style={{ color: 'var(--text-muted)' }}>•</span>
                              <span>{new Date(currentSession.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </p>
                          </div>
                        )}
                      </div>
                      <button
                        className="hero-quick-menu"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                          padding: '0.5rem',
                          borderRadius: 'var(--radius-md)',
                          transition: 'all var(--transition-fast)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '40px',
                          minHeight: '40px'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          alert('Quick actions menu - Coming soon!');
                        }}
                        title="More options"
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                          <circle cx="10" cy="4" r="1.5" />
                          <circle cx="10" cy="10" r="1.5" />
                          <circle cx="10" cy="16" r="1.5" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={4} className="d-flex justify-content-center justify-content-lg-end">
                  <Link
                    to="/personnel/check"
                    className="btn-hero btn-primary-modern"
                    style={{
                      width: '100%',
                      maxWidth: window.innerWidth < 768 ? '100%' : '280px',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.75rem',
                      minHeight: window.innerWidth < 768 ? '56px' : '48px',
                      ...(isCheckedIn && {
                        background: 'linear-gradient(135deg, #dc3545, #c82333)',
                        boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)'
                      })
                    }}
                  >
                    {isCheckedIn ? (
                      <>
                        <FaSignOutAlt size={window.innerWidth < 768 ? 20 : 24} />
                        <span>Check Out</span>
                      </>
                    ) : (
                      <>
                        <FaSignInAlt size={window.innerWidth < 768 ? 20 : 24} />
                        <span>Check In</span>
                      </>
                    )}
                  </Link>
                </Col>
              </Row>
            </div>

            {/* KPI Cards Grid */}
            <Row className="g-3 g-md-4 mb-4 animate-stagger">
              {/* Total Hours Card */}
              <Col xs={12} sm={6} lg={4}>
                <div className="kpi-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="kpi-icon kpi-icon-blue">
                      <FaClock />
                    </div>
                    <div className="kpi-trend kpi-trend-up">
                      <FaArrowUp size={14} />
                      <span>{stats.hoursChange}%</span>
                    </div>
                  </div>
                  <div>
                    <div className="kpi-label">Total Hours</div>
                    <div className="kpi-metric">{stats.totalHours}</div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                      This month
                    </div>
                  </div>
                  {/* Mini sparkline */}
                  <div style={{
                    height: '40px',
                    background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))',
                    borderRadius: 'var(--radius-md)',
                    marginTop: '1rem',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '60%',
                      background: 'linear-gradient(to top, var(--accent-blue), transparent)',
                      opacity: 0.3
                    }}></div>
                  </div>
                </div>
              </Col>

              {/* Sites Visited Card */}
              <Col xs={12} sm={6} lg={4}>
                <div className="kpi-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="kpi-icon kpi-icon-success">
                      <FaMapMarkedAlt />
                    </div>
                    <div className="kpi-trend kpi-trend-up">
                      <FaArrowUp size={14} />
                      <span>{stats.sitesChange}%</span>
                    </div>
                  </div>
                  <div>
                    <div className="kpi-label">Sites Visited</div>
                    <div className="kpi-metric" style={{
                      background: 'linear-gradient(135deg, var(--success-emerald), var(--success-light))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      {stats.sitesVisited}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                      This month
                    </div>
                  </div>
                  {/* Progress ring visual */}
                  <div style={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginTop: '1rem'
                  }}>
                    <div style={{
                      flex: 1,
                      height: '8px',
                      background: 'var(--bg-tertiary)',
                      borderRadius: 'var(--radius-full)',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${Math.min((stats.sitesVisited / 150) * 100, 100)}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, var(--success-emerald), var(--success-light))',
                        borderRadius: 'var(--radius-full)',
                        transition: 'width 1s ease-out'
                      }}></div>
                    </div>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {Math.min((stats.sitesVisited / 150) * 100, 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </Col>

              {/* Reports Submitted Card */}
              <Col xs={12} sm={6} lg={4}>
                <div className="kpi-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="kpi-icon kpi-icon-warning">
                      <FaClipboardCheck />
                    </div>
                    <div className="kpi-trend kpi-trend-up">
                      <FaFire size={14} />
                      <span>{stats.reportsChange}%</span>
                    </div>
                  </div>
                  <div>
                    <div className="kpi-label">Reports Submitted</div>
                    <div className="kpi-metric" style={{
                      background: 'linear-gradient(135deg, var(--warning-amber), var(--warning-light))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      {stats.reportsSubmitted}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                      This month
                    </div>
                  </div>
                  {/* Activity indicator */}
                  <div style={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    marginTop: '1rem'
                  }}>
                    {[...Array(7)].map((_, i) => (
                      <div key={i} style={{
                        flex: 1,
                        height: `${Math.random() * 100}%`,
                        background: 'linear-gradient(to top, var(--warning-amber), var(--warning-light))',
                        borderRadius: 'var(--radius-sm)',
                        opacity: 0.6,
                        animation: `fade-in 0.5s ease-out ${i * 0.1}s forwards`
                      }}></div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Assigned Sites Table/Cards */}
            <div className="glass-card animate-fade-in" style={{ marginTop: '2rem' }}>
              <div style={{
                padding: window.innerWidth < 768 ? '1rem' : 'var(--spacing-xl)',
                borderBottom: '1px solid var(--border-color)'
              }}>
                <div>
                  <h3 style={{
                    fontSize: window.innerWidth < 768 ? 'var(--font-size-xl)' : 'var(--font-size-2xl)',
                    fontWeight: 700,
                    marginBottom: '0.25rem',
                    color: 'var(--text-primary)'
                  }}>
                    Your Assigned Sites
                  </h3>
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: 0 }}>
                    {recentSites.length} sites assigned
                  </p>
                </div>
              </div>

              <div style={{ padding: window.innerWidth < 768 ? '0.75rem' : 'var(--spacing-lg)' }}>
                {recentSites.length > 0 ? (
                  <>
                    {/* Desktop Table View */}
                    <div className="table-responsive d-none d-md-block">
                      <table className="modern-table">
                        <thead>
                          <tr>
                            <th>Site Name</th>
                            <th>Address</th>
                            <th>Last Visited</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentSites.map(site => (
                            <tr key={site.id}>
                              <td>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                  {site.name}
                                </div>
                              </td>
                              <td style={{ color: 'var(--text-secondary)' }}>
                                {site.address}
                              </td>
                              <td style={{ color: 'var(--text-secondary)' }}>
                                {site.lastVisited}
                              </td>
                              <td>
                                <span className={`status-badge ${site.status === 'visited' ? 'status-badge-success' : 'status-badge-error status-badge-pulse'
                                  }`}>
                                  {site.status === 'visited' ? '✓ Visited' : '! Not Visited'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="d-md-none sites-card-list">
                      {recentSites.map((site, index) => (
                        <SiteCard
                          key={site.id}
                          site={site}
                          index={index}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <Alert variant="info" style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid var(--accent-blue)',
                    borderRadius: 'var(--radius-lg)',
                    textAlign: 'center',
                    padding: 'var(--spacing-xl)'
                  }}>
                    You don't have any sites assigned yet. Please contact your administrator.
                  </Alert>
                )}
              </div>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default PersonnelDashboardModern;
